import type { Key } from "react";
import type { LocationItem } from "./types";

import { useCallback, useEffect, useRef } from "react";
import { LocationListItem } from "./LocationListItem";

interface LocationsListProps {
  locations: LocationItem[];
  searchQuery?: string;
  selectedLocationId?: Key | null;
  onSelectLocation?: (id: Key) => void;
}

export function LocationsList({
  locations,
  searchQuery,
  selectedLocationId,
  onSelectLocation,
}: LocationsListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<Key, HTMLDivElement>>(new Map());

  const setCardRef = useCallback(
    (id: Key) => (el: HTMLDivElement | null) => {
      if (el) {
        cardRefs.current.set(id, el);
      } else {
        cardRefs.current.delete(id);
      }
    },
    [],
  );

  useEffect(() => {
    if (selectedLocationId == null) {
      return;
    }
    const el = cardRefs.current.get(selectedLocationId);
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const headerOffset = 144; // utility bar (48px) + navbar (80px) + 16px buffer
    const isAboveViewport = rect.top < headerOffset;
    const isBelowViewport = rect.bottom > window.innerHeight;

    if (isAboveViewport || isBelowViewport) {
      const desiredTop = window.scrollY + rect.top - headerOffset;

      // Don't scroll past the point where the list container's bottom reaches
      // the viewport bottom — prevents over-scrolling into the footer.
      const listEl = listRef.current;
      const maxScrollTop = listEl
        ? listEl.getBoundingClientRect().bottom +
          window.scrollY -
          window.innerHeight +
          32 // 32px buffer to prevent over-scrolling into the footer
        : Infinity;

      window.scrollTo({
        behavior: "smooth",
        top: Math.min(desiredTop, maxScrollTop),
      });
    }
  }, [selectedLocationId]);

  return (
    <div className="locations-list flex flex-col gap-6" ref={listRef}>
      <p className="font-bold text-md text-new-beginnings-base leading-[32px]">
        Showing {locations.length} locations
        {searchQuery ? ` for "${searchQuery}"` : ""}
      </p>

      {locations.map((location) => (
        <LocationListItem
          isSelected={selectedLocationId === location.id}
          key={location.id}
          location={location}
          onSelect={() => onSelectLocation?.(location.id)}
          ref={setCardRef(location.id)}
        />
      ))}
    </div>
  );
}
